import React from 'react';
import { useCounter } from './Home';

const activities = [
  { c:'red',  title:'Targeted Campus Marketing',    body:'Strategic brand positioning within educational environments to reach the right audience at the right time.',     svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#FF0032" strokeWidth="1.5"/><circle cx="12" cy="12" r="5" stroke="#FF0032" strokeWidth="1.5"/><circle cx="12" cy="12" r="1.8" fill="#FF0032"/></svg> },
  { c:'blue', title:'BTL Promotional Campaigns',    body:'Below-the-line marketing initiatives designed specifically for campus engagement and brand visibility.',          svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 20V10M12 20V4M6 20v-6" stroke="#0583F2" strokeWidth="2" strokeLinecap="round"/></svg> },
  { c:'red',  title:'Student Ambassador Programs',  body:'Building authentic brand relationships through student influencers and campus representatives.',                  svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#FF0032" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="#FF0032" strokeWidth="1.5"/></svg> },
  { c:'blue', title:'Experiential Marketing',       body:'Creating memorable brand experiences through interactive events and product demonstrations on campus.',           svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M5 3l14 9-14 9V3z" stroke="#0583F2" strokeWidth="1.5" strokeLinejoin="round"/></svg> },
  { c:'red',  title:'Digital Campus Presence',      body:'Leveraging social media and digital platforms for enhanced campus brand awareness and engagement.',               svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="#FF0032" strokeWidth="1.5"/><path d="M8 21h8M12 17v4" stroke="#FF0032" strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { c:'blue', title:'Innovation Workshops',          body:'Collaborative sessions connecting brands with student creativity and fresh perspectives for real impact.',        svg:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="#0583F2" strokeWidth="1.5"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3" stroke="#0583F2" strokeWidth="1.5" strokeLinecap="round"/></svg> },
];

const impactStats = [
  { num:'300', suffix:'%', label:'Brand Visibility',   color:'var(--red)'  },
  { num:'85',  suffix:'%', label:'Student Engagement', color:'var(--blue)' },
  { num:'50',  suffix:'+', label:'Campus Reach',       color:'var(--red)'  },
  { num:'100', suffix:'+', label:'Active Campaigns',   color:'var(--blue)' },
];

const process = [
  { n:'01', c:'red',  title:'Brand Discovery',  desc:'Understanding your vision, values, and campaign goals.'       },
  { n:'02', c:'blue', title:'Campus Strategy',   desc:'Tailored engagement plans built for each institution.'        },
  { n:'03', c:'red',  title:'Campaign Launch',   desc:'Executing activations with precision on the ground.'         },
  { n:'04', c:'blue', title:'Results & Growth',  desc:'Measuring success and scaling what performs best.'           },
];

function ImpactStat({ num, suffix, label, color }) {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold:.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const val = useCounter(num, inView);
  return (
    <div ref={ref} className="card p-8 text-center red-hover">
      <div className="stat-num" style={{ color }}>{val}{suffix}</div>
      <div className="mt-3 text-white/45 text-sm font-medium">{label}</div>
    </div>
  );
}

export default function ChalkingBrands() {
  return (
    <section id="chalking-brands" className="section bg-surface">
      <div className="max-w-content mx-auto">

        {/* Header */}
        <div className="max-w-2xl mb-16 reveal">
          <span className="label label-red">Brand Strategy</span>
          <h2 className="heading mt-5">Chalking Out <em>Brands</em></h2>
          <div className="divider wide mt-5 mb-5" />
          <p className="subtext text-base">
            Empowering brands to establish meaningful connections with student communities
            through strategic campus marketing initiatives.
          </p>
        </div>

        {/* 6 activity cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {activities.map((a, i) => (
            <div key={a.title}
              className={`card relative overflow-hidden p-8 ${a.c === 'red' ? 'red-hover' : 'blue-hover'} reveal d${(i%3)+1}`}>
              <div className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background: a.c === 'red' ? 'var(--red)' : 'var(--blue)' }} />
              <div className={`icon-box ${a.c} mb-5`}>{a.svg}</div>
              <h4 className="text-white font-bold text-base mb-3">{a.title}</h4>
              <p className="subtext text-sm leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>

        {/* Impact stats */}
        <div className="mb-16">
          <div className="flex items-end justify-between mb-8 reveal">
            <div>
              <h3 className="text-white font-bold text-xl">Brand Impact Across Campuses</h3>
              <div className="divider mt-3" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {impactStats.map(s => <ImpactStat key={s.label} {...s} />)}
          </div>
        </div>

        {/* How we work */}
        <div>
          <div className="flex items-end justify-between mb-8 reveal">
            <div>
              <h3 className="text-white font-bold text-xl">How We Work With Brands</h3>
              <div className="divider blue mt-3" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((p, i) => (
              <div key={p.n}
                className={`card card-dark p-7 ${p.c === 'red' ? 'red-hover' : 'blue-hover'} reveal d${i+1}`}>
                <div className="text-5xl font-black opacity-[.07] text-white leading-none mb-3 select-none"
                  style={{ fontFamily:'Playfair Display,serif' }}>{p.n}</div>
                <div className="w-8 h-[2px] mb-4" style={{ background: p.c === 'red' ? 'var(--red)' : 'var(--blue)' }} />
                <h4 className="text-white font-bold text-sm mb-2">{p.title}</h4>
                <p className="text-white/42 text-xs leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
